import React, { useState, useEffect } from 'react'
import { Modal, Input, Slider, Button, Icon, Spin } from 'antd'
import './AudioSegmentEditModal.scss'

// Shared Helpers & Components
import { T, fmtMs, speakerColor, speakerName } from './SharedLayout'
import InlineAudioPlayer from './InlineAudioPlayer'

const { TextArea } = Input

const AudioSegmentEditModal = ({
  visible,
  onClose,
  segment,
  segmentIndex,
  editor,
  characterMap
}) => {
  const [localText, setLocalText] = useState(segment?.text || '')
  const isPause = segment?.type === 'pause'
  const isVoice = segment?.type === 'voice'
  const [pauseMs, setPauseMs] = useState(segment?.duration_ms || 200)

  useEffect(() => {
    if (visible) {
      setLocalText(editor.getPendingText(segmentIndex) || segment?.text || '')
      if (isPause) setPauseMs(segment?.duration_ms || 200)
    }
  }, [visible, segmentIndex, segment, isPause, editor])

  const color = speakerColor(segment?.speaker)
  const name = speakerName(segment?.speaker, characterMap)
  const loading = editor.isSegmentLoading(segmentIndex)
  const hasPending = editor.hasPendingEdit(segmentIndex)
  const hasErr = editor.hasError(segmentIndex)

  const handleTextChange = e => {
    const val = e.target.value
    setLocalText(val)
    editor.previewTextEdit(segmentIndex, val)
  }

  const handleCommit = () => editor.commitTextEdit(segmentIndex, localText)
  const handleRegenerate = () => editor.regenerateSegment({ segmentIndex })
  const handlePauseCommit = () => editor.updatePause(segmentIndex, pauseMs)

  return (
    <Modal
      title={isPause ? 'Edit Pause' : `Edit ${name}`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={500}
      className="audio-segment-edit-modal"
      centered
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Header info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{ width: 3, height: 20, borderRadius: 2, background: color }}
          />
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color,
              textTransform: 'uppercase'
            }}
          >
            {isPause ? 'Pause Duration' : name}
          </div>
          {isVoice && (
            <span style={{ fontSize: 11, color: T.inkLight }}>
              {fmtMs(segment.start_ms)} – {fmtMs(segment.end_ms)}
            </span>
          )}
          {loading && <Spin size="small" style={{ marginLeft: 'auto' }} />}
        </div>

        {/* Voice content */}
        {isVoice && (
          <>
            <TextArea
              rows={4}
              value={localText}
              onChange={handleTextChange}
              disabled={loading}
              placeholder="Enter voiceover text..."
              style={{
                fontSize: 14,
                lineHeight: 1.6,
                borderRadius: 8,
                resize: 'none',
                fontFamily: T.font,
                borderColor: hasPending ? T.orange : T.border
              }}
            />
            {hasPending && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  color: T.orange,
                  fontSize: 12
                }}
              >
                <Icon type="warning" />
                Text changed, audio needs regeneration
              </div>
            )}
            {hasErr && (
              <div style={{ color: T.red, fontSize: 12 }}>
                Generation failed. Please try again.
              </div>
            )}
            {segment.audio_url && (
              <InlineAudioPlayer
                src={segment.audio_url}
                accent={color}
                label="Current Audio preview"
              />
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <Button
                type="primary"
                block
                onClick={handleCommit}
                loading={loading}
                disabled={!hasPending || loading}
                style={{ height: 40, background: T.ink, borderColor: T.ink }}
              >
                Save & Regenerate
              </Button>
              <Button
                block
                onClick={handleRegenerate}
                loading={loading}
                disabled={loading}
                style={{ height: 40 }}
              >
                New Take
              </Button>
            </div>
          </>
        )}

        {/* Pause content */}
        {isPause && (
          <div style={{ padding: '0 8px' }}>
            <div style={{ fontSize: 12, color: T.inkSub, marginBottom: 20 }}>
              Adjust the silence between segments (100ms – 2000ms)
            </div>
            <Slider
              value={pauseMs}
              onChange={setPauseMs}
              min={100}
              max={2000}
              step={50}
              marks={{ 100: '0.1s', 500: '0.5s', 1000: '1s', 2000: '2s' }}
              disabled={loading}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 24
              }}
            >
              <span style={{ fontWeight: 600, color: T.inkMid }}>
                {pauseMs}ms
              </span>
              <Button
                type="primary"
                onClick={handlePauseCommit}
                loading={loading}
                disabled={loading || pauseMs === segment?.duration_ms}
                style={{ background: T.ink, borderColor: T.ink }}
              >
                Apply Change
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default AudioSegmentEditModal
